import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../../context/AuthContext';
import { FaEdit, FaTrash, FaEye, FaEyeSlash, FaStar, FaPhone } from 'react-icons/fa';
import { toast } from 'react-toastify';
import Alert from '../common/Alert';
import IconButton from '../common/IconButton';
import FormInput from '../common/FormInput';
import useApi from '../../hooks/useApi';
import RateHelper from './RateHelper';
import CommunityIdentity from '../common/CommunityIdentity';
import 'animate.css';

function DomesticHelp() {
  const { session } = useContext(AuthContext);
  const { roles, communityId } = session || { roles: ['GUEST'], communityId: null };
  const isSuperAdmin = roles.includes('SUPER_ADMIN');
  const isAdmin = roles.includes('ADMIN');
  const isUser = roles.includes('USER') || isAdmin || isSuperAdmin;
  const { get, post, put, del } = useApi();

  const [helpers, setHelpers] = useState([]);
  const [communities, setCommunities] = useState([]);
  const [selectedCommunityId, setSelectedCommunityId] = useState(isSuperAdmin ? '' : communityId?.toString() || '');
  const [formData, setFormData] = useState({
    name: '',
    contactNo: '',
    category: '',
    isAvailable: false,
    communities: isSuperAdmin ? [] : communityId ? [{ id: communityId }] : [],
  });
  const [filters, setFilters] = useState({
    category: '',
    isAvailable: '',
    minRating: '',
  });
  const [error, setError] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [ratingHelper, setRatingHelper] = useState(null);

  useEffect(() => {
    if (isSuperAdmin) {
      fetchCommunities();
    }
    fetchHelpers();
  }, [selectedCommunityId, filters]);

  const fetchCommunities = async () => {
    try {
      const response = await get('/api/communities/active');
      if (!Array.isArray(response)) {
        throw new Error('Invalid communities response format');
      }
      setCommunities(response);
    } catch (err) {
      console.error('DomesticHelp - Fetch communities error:', err);
      setError('Failed to load communities.');
      toast.error('Failed to load communities.');
    }
  };

  const fetchHelpers = async () => {
    try {
      const params = {
        ...(isSuperAdmin && selectedCommunityId ? { communityId: selectedCommunityId } : !isSuperAdmin && communityId ? { communityId } : {}),
        ...(filters.category && { category: filters.category }),
        ...(filters.isAvailable !== '' && { isAvailable: filters.isAvailable }),
        ...(filters.minRating && { minRating: filters.minRating }),
      };
      const response = await get('/api/domestic-helpers', { params });
      setHelpers(response || []);
      setError('');
    } catch (err) {
      console.error('DomesticHelp - error:', err);
      const errorMessage = err.response?.data?.message || 'Failed to fetch helpers.';
      setError(errorMessage);
      toast.error(errorMessage);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
    console.log('Form data updated:', { ...formData, [name]: type === 'checkbox' ? checked : value });
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters({ ...filters, [name]: value });
  };

  const handleCommunityChange = (e) => {
    const newCommunityId = e.target.value;
    setSelectedCommunityId(newCommunityId);
    if (!isSuperAdmin) {
      setFormData({
        ...formData,
        communities: newCommunityId ? [{ id: newCommunityId }] : [{ id: communityId }],
      });
    }
  };

  const handleCommunityIdsChange = (e) => {
    const options = e.target.options;
    const selectedCommunities = Array.from(options)
      .filter((option) => option.selected)
      .map((option) => ({ id: option.value }));
    setFormData({ ...formData, communities: selectedCommunities });
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.name) errors.name = 'Name is required.';
    if (!formData.contactNo || !/^\d{10}$/.test(formData.contactNo))
      errors.contactNo = 'Contact number must be 10 digits.';
    if (!formData.category) errors.category = 'Category is required.';
    if (isSuperAdmin && !editingId && formData.communities.length === 0)
      errors.communities = 'At least one community must be selected.';
    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      const errorMsg = Object.values(validationErrors).join(' ');
      setError(errorMsg);
      toast.error(errorMsg);
      return;
    }

    try {
      const payload = {
        ...formData,
        isAvailable: formData.isAvailable,
      };
      console.log('Submitting payload:', payload);
      if (editingId) {
        const response = await put(`/api/domestic-helpers/${editingId}`, payload);
        setHelpers(
          helpers.map((helper) =>
            helper.id === editingId ? { ...helper, ...response } : helper
          )
        );
        toast.success('Helper updated successfully');
        setEditingId(null);
      } else {
        const response = await post('/api/domestic-helpers', payload);
        setHelpers([...helpers, response]);
        toast.success('Helper added successfully');
      }
      setFormData({
        name: '',
        contactNo: '',
        category: '',
        isAvailable: false,
        communities: isSuperAdmin ? [] : communityId ? [{ id: communityId }] : [],
      });
      setError('');
    } catch (err) {
      console.error('DomesticHelp - Submit error:', err);
      const errorMessage = err.response?.data?.message || `Failed to ${editingId ? 'update' : 'add'} helper.`;
      setError(errorMessage);
      toast.error(errorMessage);
    }
  };

  const handleEdit = (helper) => {
    const updatedFormData = {
      name: helper.name,
      contactNo: helper.contactNo,
      category: helper.category,
      isAvailable: helper.isAvailable,
      communities: helper.communities.map(c => ({ id: c.id.toString() })),
    };
    setFormData(updatedFormData);
    setEditingId(helper.id);
    console.log('Editing helper:', helper, 'Form data set to:', updatedFormData);
    if (isSuperAdmin) {
      setSelectedCommunityId('');
    }
  };

  const handleDelete = async (id) => {
    try {
      await del(`/api/domestic-helpers/${id}`);
      setHelpers(helpers.filter((helper) => helper.id !== id));
      toast.success('Helper deleted successfully');
      setError('');
    } catch (err) {
      console.error('DomesticHelp - deleted error:', err);
      const errorMessage = err.response?.data?.message || 'Failed to delete helper.';
      setError(errorMessage);
      toast.error(errorMessage);
    }
  };

  const handleToggleVisibility = async (helper) => {
    try {
      const updatedHelper = { ...helper, isVisible: !helper.isVisible };
      await put(`/api/domestic-helpers/${helper.id}/visibility`, { isVisible: updatedHelper.isVisible });
      setHelpers(
        helpers.map((h) => (h.id === helper.id ? updatedHelper : h))
      );
      toast.success(`Helper visibility ${updatedHelper.isVisible ? 'enabled' : 'disabled'} successfully`);
    } catch (err) {
      console.error('DomesticHelp - Toggle visibility error:', err);
      const errorMessage = err.response?.data?.message || 'Failed to toggle visibility.';
      setError(errorMessage);
      toast.error(errorMessage);
    }
  };

  const handleRateHelper = (helper) => {
    setRatingHelper(helper);
  };

  const handleRatingSubmitted = () => {
    fetchHelpers();
    setRatingHelper(null);
    toast.success('Rating submitted successfully');
  };

  if (!isUser) {
    return <Alert type="warning" message="Access denied. You must be logged in to view domestic helpers." />;
  }

  return (
    <div className="container mt-5 animate__animated animate__fadeIn">
      <h2 className="mb-4">Domestic Help</h2>
      {error && <Alert type="danger" message={error} />}

      {(isAdmin || isSuperAdmin) && (
        <div className="card shadow mb-4">
          <div className="card-body">
            <h5 className="card-title">{editingId ? 'Edit Helper' : 'Add Helper'}</h5>
            <form onSubmit={handleSubmit}>
              <div className="mb-3">
                <FormInput
                  label="Name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  placeholder="Enter helper's name"
                />
              </div>
              <div className="mb-3">
                <FormInput
                  label="Contact Number"
                  name="contactNo"
                  value={formData.contactNo}
                  onChange={handleChange}
                  required
                  placeholder="Enter 10-digit contact number"
                />
              </div>
              <div className="mb-3">
                <label htmlFor="category" className="form-label">
                  Category
                </label>
                <select
                  id="category"
                  name="category"
                  value={formData.category}
                  onChange={handleChange}
                  className="form-select"
                  required
                >
                  <option value="">Select Category</option>
                  <option value="ELECTRICIAN">Electrician</option>
                  <option value="PLUMBER">Plumber</option>
                  <option value="CLEANER">Cleaner</option>
                  <option value="OTHER">Other</option>
                </select>
              </div>
              <div className="mb-3 form-check">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id="isAvailable"
                  name="isAvailable"
                  checked={formData.isAvailable}
                  onChange={handleChange}
                />
                <label className="form-check-label" htmlFor="isAvailable">
                  Available
                </label>
              </div>
              {isSuperAdmin && (
                <div className="mb-3">
                  <label htmlFor="communities" className="form-label">
                    Visible to Communities
                  </label>
                  <select
                    id="communities"
                    name="communities"
                    multiple
                    value={formData.communities.map(c => c.id)}
                    onChange={handleCommunityIdsChange}
                    className="form-select"
                    disabled={communities.length === 0}
                    required={!editingId}
                  >
                    {communities.map((community) => (
                      <option key={community.id} value={community.id}>
                        {community.displayName}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <div className="d-flex gap-2">
                <button type="submit" className="btn btn-primary flex-grow-1">
                  {editingId ? 'Update Helper' : 'Add Helper'}
                </button>
                <button
                  type="button"
                  className="btn btn-secondary flex-grow-1"
                  onClick={() => {
                    setFormData({
                      name: '',
                      contactNo: '',
                      category: '',
                      isAvailable: false,
                      communities: isSuperAdmin ? [] : communityId ? [{ id: communityId }] : [],
                    });
                    setEditingId(null);
                    setError('');
                  }}
                >
                  Clear
                </button>
                {editingId && (
                  <button
                    type="button"
                    className="btn btn-secondary flex-grow-1"
                    onClick={() => {
                      setFormData({
                        name: '',
                        contactNo: '',
                        category: '',
                        isAvailable: false,
                        communities: isSuperAdmin ? [] : communityId ? [{ id: communityId }] : [],
                      });
                      setEditingId(null);
                      setError('');
                    }}
                  >
                    Cancel
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="card shadow mb-4">
        <div className="card-body">
          <h5 className="card-title">Filter Helpers</h5>
          <div className="d-flex flex-wrap gap-3">
            {isSuperAdmin && (
              <div className="mb-3 flex-fill" style={{ minWidth: '200px' }}>
                <label htmlFor="viewCommunityFilter" className="form-label">
                  Community
                </label>
                <select
                  id="viewCommunityFilter"
                  name="communityId"
                  value={selectedCommunityId}
                  onChange={handleCommunityChange}
                  className="form-select"
                  disabled={communities.length === 0}
                >
                  <option value="">All Communities</option>
                  {communities.map((community) => (
                    <option key={community.id} value={community.id}>
                      {community.displayName}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div className="mb-3 flex-fill" style={{ minWidth: '200px' }}>
              <label htmlFor="filterCategory" className="form-label">
                Category
              </label>
              <select
                id="filterCategory"
                name="category"
                value={filters.category}
                onChange={handleFilterChange}
                className="form-select"
              >
                <option value="">All Categories</option>
                <option value="ELECTRICIAN">Electrician</option>
                <option value="PLUMBER">Plumber</option>
                <option value="CLEANER">Cleaner</option>
                <option value="OTHER">Other</option>
              </select>
            </div>
            <div className="mb-3 flex-fill" style={{ minWidth: '200px' }}>
              <label htmlFor="filterAvailability" className="form-label">
                Availability
              </label>
              <select
                id="filterAvailability"
                name="isAvailable"
                value={filters.isAvailable}
                onChange={handleFilterChange}
                className="form-select"
              >
                <option value="">All</option>
                <option value="true">Available</option>
                <option value="false">Unavailable</option>
              </select>
            </div>
            <div className="mb-3 flex-fill" style={{ minWidth: '200px' }}>
              <label htmlFor="filterMinRating" className="form-label">
                Minimum Rating
              </label>
              <select
                id="filterMinRating"
                name="minRating"
                value={filters.minRating}
                onChange={handleFilterChange}
                className="form-select"
              >
                <option value="">Any Rating</option>
                <option value="1">1 Star</option>
                <option value="2">2 Stars</option>
                <option value="3">3 Stars</option>
                <option value="4">4 Stars</option>
                <option value="5">5 Stars</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      <div className="row">
        {helpers.length === 0 ? (
          <div className="col-12">
            <Alert type="info" message="No domestic helpers available." />
          </div>
        ) : (
          helpers.map((helper) => (
            <div key={helper.id} className="col-md-6 mb-4">
              <div className="card shadow">
                <div className="card-body">
                  <h5 className="card-title">{helper.name}</h5>
                  <p className="card-text">
                    <strong>Contact:</strong>{' '}
                    {helper.contactNo}{' '}
                    <a
                      href={`tel:${helper.contactNo.replace(/\D/g, '')}`}
                      title="Call Helper"
                      aria-label={`Call ${helper.name} at ${helper.contactNo}`}
                      style={{ color: '#28a745', textDecoration: 'none' }}
                    >
                      <FaPhone />
                    </a>
                    <br />
                    <strong>Category:</strong> {helper.category}<br />
                    <strong>Availability:</strong>{' '}
                    <span style={{ color: helper.isAvailable ? 'green' : 'red' }}>
                      ● {helper.isAvailable ? 'Available' : 'Unavailable'}
                    </span><br />
                    <strong>Rating:</strong>{' '}
                    {helper.averageRating ? (
                      <>
                        {Array(Math.round(helper.averageRating)).fill().map((_, i) => (
                          <FaStar key={i} color="gold" />
                        ))}
                        {' '}{helper.averageRating.toFixed(1)} ({helper.ratingCount} reviews)
                      </>
                    ) : (
                      'No ratings yet'
                    )}<br />
                    {isSuperAdmin && (
                      <>
                        <strong>Visible:</strong> {helper.isVisible ? 'Yes' : 'No'}<br />
                        <strong>Communities:</strong>
                        <ul style={{ listStyleType: 'none', paddingLeft: '0', marginTop: '0' }}>
                          {helper.communities.map((community, index) => (
                            <li key={community.id} className="mb-1">
                              <span className="me-1">{index + 1}.</span>
                              <CommunityIdentity community={community} size="xs" compact />
                            </li>
                          ))}
                        </ul>
                      </>
                    )}
                  </p>
                  <div className="mt-2 d-flex gap-2">
                    {isUser && (
                      <IconButton
                        icon={<FaStar />}
                        tooltip="Rate Helper"
                        variant="primary"
                        onClick={() => handleRateHelper(helper)}
                        className="me-2"
                      />
                    )}
                    {(isAdmin || isSuperAdmin) && (
                      <>
                        <IconButton
                          icon={<FaEdit />}
                          tooltip="Edit"
                          variant="warning"
                          onClick={() => handleEdit(helper)}
                        />
                        <IconButton
                          icon={<FaTrash />}
                          tooltip="Delete"
                          variant="danger"
                          onClick={() => handleDelete(helper.id)}
                          className="me-2"
                        />
                        {isSuperAdmin && (
                          <IconButton
                            icon={helper.isVisible ? <FaEyeSlash /> : <FaEye />}
                            tooltip={helper.isVisible ? 'Hide' : 'Show'}
                            variant={helper.isVisible ? 'secondary' : 'success'}
                            onClick={() => handleToggleVisibility(helper)}
                          />
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {ratingHelper && (
        <RateHelper
          helper={ratingHelper}
          onClose={() => setRatingHelper(null)}
          onSubmit={handleRatingSubmitted}
        />
      )}
    </div>
  );
}

export default DomesticHelp;
